import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../../../api/apiClient";

export function ServicePortalImageGenerator({ serviceName, description, onSelect }: {
  serviceName: string; description: string; onSelect: (file?: File) => void;
}) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => {
    let cancelled = false;
    api<{ enabled: boolean }>("/api/ai/service-image/availability", { suppressLoading: true, notifyError: false })
      .then(result => { if (!cancelled) setAvailable(result.enabled); })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; requestRef.current?.abort(); };
  }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const generate = async () => {
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true); setError("");
    try {
      const blob = await api<Blob>("/api/ai/service-image", {
        method: "POST", responseType: "blob", signal: controller.signal, suppressLoading: true, notifyError: false,
        body: { serviceName: serviceName.trim(), description: description.slice(0, 1000), imageDescription: prompt.trim() || null },
      });
      if (controller.signal.aborted) return;
      const file = new File([blob], "generated-portal-image.jpg", { type: "image/jpeg" });
      setGenerated(file); setPreview(URL.createObjectURL(file));
    } catch (err) {
      if (!controller.signal.aborted) setError(err instanceof Error ? err.message : "The image could not be generated.");
    } finally { if (!controller.signal.aborted) setLoading(false); }
  };

  return <div className="service-image-generator">
    <label className="form-label" htmlFor="portal-image-idea">Or generate an image</label>
    <textarea id="portal-image-idea" className="form-input" rows={2} maxLength={500} value={prompt}
      disabled={loading} onChange={event => setPrompt(event.target.value)} placeholder="Optional: describe the image you would like, such as fresh vegetables and food parcels" />
    <p className="field-hint">Uses the service name and description. Preview the result before choosing it.</p>
    <div className="button-row button-row-left">
      <button type="button" className="action-button compact" disabled={!available || !serviceName.trim() || loading} onClick={() => void generate()}>
        {loading ? "Generating image…" : generated ? "Generate another image" : "Generate image"}
      </button>
      {loading && <button type="button" className="action-button compact secondary" onClick={() => { requestRef.current?.abort(); setLoading(false); }}>Cancel</button>}
    </div>
    {available === null && <p role="status" className="field-hint">Checking image generation…</p>}
    {available === false && <p className="field-hint">Image generation is unavailable. You can still upload an image.</p>}
    {!serviceName.trim() && <p className="field-hint">Enter a service name in Details first.</p>}
    {loading && <p role="status" className="field-hint">Creating your image. This may take a few minutes.</p>}
    {error && <p role="alert" className="field-hint">{error}</p>}
    {preview && generated && <div className="service-generated-image">
      <img src={preview} alt="Generated community portal image preview" />
      <p className="field-hint">AI-generated illustration. Choose it to see the portal card preview, then save the service to publish the change.</p>
      <button type="button" className="action-button compact" disabled={loading} onClick={() => { onSelect(generated); setGenerated(null); setPreview(null); }}>Use this image</button>
    </div>}
  </div>;
}
